import { createContext, useContext, useMemo, type ReactNode } from "react";
import { createTranslator, DEFAULT_LOCALE, type SupportedLocale, type Translator } from "./index";

const LocaleContext = createContext<SupportedLocale>(DEFAULT_LOCALE);

/**
 * Wraps the app in the resolved merchant locale. Mounted once in root.tsx's
 * `Layout` (which renders for both the success tree AND nested route
 * ErrorBoundaries), so every screen under `/app/*` can call `useTranslation()`.
 */
export function LocaleProvider({
  locale,
  children,
}: {
  locale: SupportedLocale;
  children: ReactNode;
}) {
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale(): SupportedLocale {
  return useContext(LocaleContext);
}

/** `const { t } = useTranslation();` then `t("dashboard.heading")`. */
export function useTranslation(): { t: Translator; locale: SupportedLocale } {
  const locale = useLocale();
  const t = useMemo(() => createTranslator(locale), [locale]);
  return { t, locale };
}
